/**
 * Shared 3DS challenge content sniff for classic Payment Link.
 *
 * Both branches must stay true at once:
 *   - Master: Nicepay HTML ACS form AND Nicepay JSON `redirectUrlHttp`,
 *     plus the SecondForm modal that must not put HTML into router state.
 *   - Feature branch: Midtrans plain redirect URL, and Nicepay HTML written
 *     via iframe.contentDocument.write (blob URLs broke Mastercard ACS).
 *
 * There is no vendor discriminator on the wire. `html_form` and `payment_url`
 * are siblings; pick by shape, then resolve the iframe load strategy.
 */

/**
 * Choose which API field to feed the 3DS modal.
 *
 * Prefer real HTML markup over `payment_url` when both exist (Nicepay HTML
 * ACS). Otherwise use `payment_url` (Nicepay JSON redirectUrlHttp or Midtrans).
 * Non-markup `html_form` is a last resort so JSON 3DS leaks still reach the
 * resolver instead of being dropped.
 *
 * @param {unknown} htmlForm
 * @param {unknown} paymentUrl
 * @returns {string|null}
 */
export function pickThreeDSChallengeContent(htmlForm, paymentUrl) {
  if (typeof htmlForm === "string" && htmlForm.trim().startsWith("<")) {
    return htmlForm;
  }
  if (typeof paymentUrl === "string" && paymentUrl.trim() !== "") {
    return paymentUrl;
  }
  if (typeof htmlForm === "string" && htmlForm.trim() !== "") {
    return htmlForm;
  }
  return null;
}

/**
 * Turn picked challenge content into an iframe strategy.
 *
 * @param {unknown} content
 * @returns {{ kind: "empty" } | { kind: "html", html: string } | { kind: "url", url: string }}
 */
export function resolveThreeDSContent(content) {
  const trimmed = typeof content === "string" ? content.trim() : "";
  if (!trimmed) return { kind: "empty" };

  if (trimmed.startsWith("<")) {
    return { kind: "html", html: content };
  }

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      const url = parsed.redirectUrlHttp || parsed.paymentUrl || parsed.payment_url;
      if (typeof url === "string" && url.trim() !== "") {
        return { kind: "url", url: url.trim() };
      }
    } catch (_) {
      // Fall through and treat the string as a URL.
    }
  }

  return { kind: "url", url: trimmed };
}
